import React, { createContext, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import MyNotification from '../components/MyNotification/MyNotification';

export const NotificationContext = createContext();

export const useNotificationContext = () => {
  const context = React.useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotificationContext must be used within a NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const { t } = useTranslation();
  const [notifications, setNotifications] = useState([]);

  const generateId = useCallback(() => {
    return Date.now() + Math.random().toString(36).substr(2, 9);
  }, []);

  const showNotification = useCallback(
    (message, type = 'info', options = {}) => {
      const id = generateId();

      const notificationData =
        typeof message === 'object' ? message : { message, type, ...options };

      const notification = {
        id,
        open: true,
        autoHide: true,
        autoHideDuration: type === 'error' ? 8000 : 6000,
        position: 'top-right',
        type: type,
        message: notificationData.message,
        title: notificationData.title || t(`notifications.titles.${type}`),
        ...notificationData,
      };

      setNotifications(prev => [...prev, notification]);

      if (notification.autoHide) {
        setTimeout(() => {
          setNotifications(prev => prev.filter(n => n.id !== id));
        }, notification.autoHideDuration);
      }

      return id;
    },
    [generateId, t]
  );

  const removeNotification = useCallback(id => {
    setNotifications(prev =>
      prev.filter(notification => notification.id !== id)
    );
  }, []);

  const clearAllNotifications = useCallback(() => {
    setNotifications([]);
  }, []);

  const value = {
    notifications,
    showNotification,
    removeNotification,
    clearAllNotifications,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}

      <div
        style={{
          position: 'fixed',
          top: 24,
          right: 24,
          zIndex: 1400,
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          alignItems: 'flex-end',
        }}
      >
        {notifications.map((notification) => (
        <MyNotification
          key={notification.id}
          open={notification.open}
          type={notification.type}
          title={notification.title}
          message={notification.message}
          autoHide={notification.autoHide}
          autoHideDuration={notification.autoHideDuration}
          position={notification.position}
          onClose={() => removeNotification(notification.id)}
            noSnackbar
        />
      ))}
      </div>
    </NotificationContext.Provider>
  );
};
